<template>
  <div class="index container">
    <transition name="fade">
      <router-view @showParent="showParent" @onReload="getResources">
      </router-view>
    </transition>

    <div v-if="showParentPage">
      <v-card elevation="0" outlined class="mt-5">
        <v-sheet class="pa-4 d-md-flex">
          <v-sheet class="flex-sm-column">
            <p class="text-h4 ma-0">{{ terminologies.classroom }}</p>
          </v-sheet>

          <v-sheet class="flex-sm-column my-3 my-md-0">
            <v-breadcrumbs class="pa-md-3 pa-0">
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'home', params: {} }"
                >Home</v-breadcrumbs-item
              >
              <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'classroom', params: {} }"
                active
              >
                {{ terminologies.classroom }}
              </v-breadcrumbs-item>
            </v-breadcrumbs>
          </v-sheet>

          <v-spacer></v-spacer>

          <v-sheet class="flex-sm-column">
            <v-row>
              <v-col cols="12" md="7">
                <v-text-field
                  dense
                  v-model="searchTerms"
                  @keyup.enter="search"
                  @click:clear="clearSearch"
                  @click:append="search"
                  rounded
                  placeholder="Search Classroom"
                  append-icon="mdi-magnify"
                  outlined
                  clearable
                  label="Search"
                  hide-details=""
                />
              </v-col>

              <v-col cols="12" md="5">
                <v-btn
                  class="align-self-end align-self-lg-start text-capitalize"
                  color="primary"
                  block
                  text
                  outlined
                  @click="classroomDialog = true"
                >
                  <v-icon>mdi-plus</v-icon>
                  Add {{ terminologies.classroom }}
                </v-btn>
              </v-col>
            </v-row>
          </v-sheet>
        </v-sheet>
      </v-card>

      <v-card elevation="0" class="mt-5">
        <div class="table-container">
          <v-data-table
            :headers="headers"
            :items="classroomList"
            :items-per-page="pagination.pageSize"
            :loading="!classroomProcessed"
            :server-items-length="pagination.totalSize"
            :page="pagination.current"
            :disable-pagination="!classroomList || classroomList.length < 1"
            :hide-default-header="!classroomList || classroomList.length < 1"
            :hide-default-footer="!classroomList || classroomList.length < 1"
            :options.sync="options"
            item-key="id"
            class="elevation-1"
            @update:options="pageChanged"
          >
            <template v-slot:item.roomNumber="{ item }">
              {{ item.roomNumber }}
              <div v-if="!globalCenter.id" class="text-caption">
                {{ getCenterName(item.centerId) }}
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon class="mr-2" @click="edit(item)"> mdi-pencil </v-icon>
              <v-icon color="red" class="mr-2" @click="remove(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>
    <confirmation-dialog ref="confirm" />

    <v-row justify="center">
      <v-dialog v-model="classroomDialog" persistent max-width="600px">
        <v-card>
          <v-card-text>
            <classroom-form
              :key="classroomId"
              :id="classroomId"
              :isModal="true"
              @clicked="closeClassroomDialog"
              @onReload="getResources"
            ></classroom-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");
const ClassroomForm = () => import("@/components/classroom/ClassroomForm.vue");

export default {
  data() {
    return {
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 100,
      },
      options: {
        //define default sorting option here.
        sortBy: [],
        sortDesc: [],
      },
      classroomList: [],
      showParentPage: true,
      classroomDialog: false,
      classroomId: null,

      headers: [
        {
          text: "Room Number/Name",
          value: "roomNumber",
          align: "start",
          sortable: true,
        },
        {
          text: "Capacity",
          value: "maxCapacity",
          align: "start",
          sortable: true,
        },
        {
          text: "Description",
          value: "description",
          align: "start",
          sortable: false,
        },

        { text: "Actions", value: "actions", align: "end", sortable: false },
      ],
      sort: {
        key: null,
      },
      searchTerms: "",
    };
  },
  components: {
    ConfirmationDialog,
    ClassroomForm,
  },
  computed: {
    ...mapGetters("classroom", ["classroomProcessed", "getClassrooms"]),
    ...mapGetters("terminology", ["terminologies"]),
    ...mapGetters("auth", ["myCenters", "getCenterName"]),
    ...mapGetters("navigation", ["globalCenter"]),
  },
  mounted() {
    this.getResources();
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("classroom", ["deleteClassroom", "fetchClassrooms"]),
    view(item) {
      this.$router.push({ name: "classroomView", params: { id: item.id } });
    },
    edit(item) {
      this.classroomId = item.id;
      this.classroomDialog = true;
    },
    async remove(item) {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Do you want to delete this classroom?  This will unassign all students assigned to this classroom.",
      );
      if (confirm) {
        this.deleteClassroom(item.id)
          .then(() => {
            this.addSuccessMessage("classroom was deleted successfully");
            this.getResources();
          })
          .catch(() => {
            this.addErrorMessage("Server response with error ");
          });
      }
    },
    getResources() {
      //default to sorting by ID desc if not defined
      const sortBy =
        this.options["sortBy"] && this.options["sortBy"].length > 0
          ? this.options.sortBy[0]
          : "id";
      const sortDesc =
        this.options["sortDesc"] && this.options["sortDesc"].length > 0
          ? this.options.sortDesc[0]
          : true;
      const params = {
        page:
          this.pagination.pageIndex && this.pagination.pageIndex > 0
            ? this.pagination.pageIndex - 1
            : 0,
        size: this.pagination.pageSize,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        centerId: this.globalCenter.id,
      };
      this.fetchClassrooms(params)
        .then((response) => {
          if (response) {
            this.pagination.totalSize = response.totalElements;
            this.classroomList = response.content;
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    showParent(show) {
      this.showParentPage = show;
      if (show) {
        // Reload the list when returning to parent view
        this.getResources();
      }
    },
    pageChanged(pagination) {
      this.pagination.pageIndex = pagination.page;
      this.pagination.pageSize = pagination.itemsPerPage;

      this.getResources();
    },
    search() {
      this.getResources();
    },
    clearSearch() {
      this.searchTerms = null;
      this.getResources();
    },
    closeClassroomDialog() {
      this.classroomDialog = false;
      this.classroomId = null;
    },
  },
  watch: {
    "pagination.numberRegisterForPage": function () {
      this.pagination.current = -1;
    },
  },
  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split("/").length;
    const fromDepth = from.path.split("/").length;
    this.showParentPage = toDepth < fromDepth;
    if (this.showParentPage) {
      this.getResources();
    }
    next();
  },
};
</script>
